import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { ApiError } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { status, login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Set once the password was right and the account asks for a second step. */
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  if (status === 'authenticated') return <Navigate to={from} replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const next = await login(identifier.trim(), password);
      if (next) {
        setChallenge(next.mfaToken);
        setPassword('');
        return;
      }
      navigate(from, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 429) setError(t('auth.rateLimited'));
      else if (caught instanceof ApiError && caught.status === 401) setError(t('auth.invalid'));
      else setError(t('auth.genericError'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyMfa(
        challenge,
        useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() },
      );
      navigate(from, { replace: true });
    } catch (caught) {
      const apiCode = caught instanceof ApiError ? caught.code : '';
      if (caught instanceof ApiError && caught.status === 429) setError(t('auth.rateLimited'));
      else if (apiCode === 'INVALID_MFA_TOKEN') {
        // The five minutes ran out: start again from the password.
        setChallenge(null);
        setCode('');
        setError(t('auth.mfaExpired'));
      } else if (apiCode === 'INVALID_CODE') setError(t('auth.mfaWrong'));
      else if (caught instanceof ApiError && caught.status === 401) {
        setChallenge(null);
        setCode('');
        setError(t('auth.invalid'));
      } else setError(t('auth.genericError'));
    } finally {
      setSubmitting(false);
    }
  }

  if (challenge) {
    return (
      <AuthLayout title={t('auth.mfaTitle')}>
        <form onSubmit={onVerify} noValidate className="space-y-4" aria-label={t('auth.mfaTitle')}>
          <p className="text-sm text-slate-700">
            {t(useRecovery ? 'auth.mfaRecoveryIntro' : 'auth.mfaIntro')}
          </p>
          <TextField
            label={t(useRecovery ? 'auth.mfaRecoveryLabel' : 'auth.mfaCodeLabel')}
            name="code"
            autoComplete="one-time-code"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoFocus
            required
            maxLength={useRecovery ? 14 : 6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || code.trim().length < (useRecovery ? 10 : 6)}
            className="w-full rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {submitting ? t('auth.signingIn') : t('auth.mfaVerify')}
          </button>
          <p className="flex justify-between text-sm">
            <button
              type="button"
              className="text-brand-700 underline"
              onClick={() => {
                setUseRecovery(!useRecovery);
                setCode('');
                setError(null);
              }}
            >
              {t(useRecovery ? 'auth.mfaUseApp' : 'auth.mfaUseRecovery')}
            </button>
            <button
              type="button"
              className="text-brand-700 underline"
              onClick={() => {
                setChallenge(null);
                setCode('');
                setError(null);
              }}
            >
              {t('auth.mfaBack')}
            </button>
          </p>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.signIn')}>
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <TextField
          label={t('auth.identifier')}
          name="identifier"
          autoComplete="username"
          autoFocus
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
        <TextField
          label={t('auth.password')}
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting || !identifier || !password}
          className="w-full rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
        >
          {submitting ? t('auth.signingIn') : t('auth.signIn')}
        </button>
        <p className="text-center text-sm">
          <Link to="/forgot-password" className="text-brand-700 underline">
            {t('auth.forgot')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
